import React, { useRef, useState, useEffect } from 'react';
import { StopCircle, Mic, Send, Paperclip, Zap, Brain, BookmarkCheck, Plus, Pin, Sparkles, Loader2 } from 'lucide-react';
import { useDragAndDrop } from '../../../../hooks';
import DragDropOverlay from '../../../common/DragDropOverlay';
import { usePromptRefiner } from '../creativeStudio/PromptRefiner';

// Files the model_service file_context_extractor can extract server-side
// (PDF with OCR fallback, DOCX, XLSX family, PPTX, images as multimodal Parts).
const EXTRACTOR_MIMES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',     // .docx
    'application/msword',                                                          // .doc
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',           // .xlsx
    'application/vnd.ms-excel',                                                    // .xls
    'application/vnd.ms-excel.sheet.macroEnabled.12',                              // .xlsm
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12',                       // .xlsb
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',        // .xltx
    'application/vnd.ms-excel.template.macroEnabled.12',                           // .xltm
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',   // .pptx
    'application/vnd.ms-powerpoint',                                               // .ppt
];

// Extension fallback for when the browser hands us an empty / generic
// `file.type` (common for .xlsm and other macro-enabled OOXML formats).
const EXTRACTOR_EXTS = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.xlsb', '.xltx', '.xltm',
    '.ppt', '.pptx',
];

const isExtractorFile = (file) => {
    if (!file) return false;
    const mime = (file.type || '').toLowerCase();
    if (mime.startsWith('image/')) return true;
    if (EXTRACTOR_MIMES.includes(mime)) return true;
    const name = (file.name || '').toLowerCase();
    return EXTRACTOR_EXTS.some((ext) => name.endsWith(ext));
};

// Plain-text-ish formats the server extractor does not handle. Refiner gets a
// short text excerpt from the start of the file (cheap, no base64 inflation).
const TEXT_LIKE_EXTS = ['.txt', '.md', '.csv', '.tsv', '.json', '.xml', '.yaml', '.yml',
    '.html', '.htm', '.css', '.js', '.jsx', '.ts', '.tsx', '.py', '.rb', '.go',
    '.java', '.c', '.cc', '.cpp', '.h', '.hpp', '.cs', '.sh', '.bash', '.sql',
    '.log', '.toml', '.ini', '.env', '.gitignore', '.rs', '.kt', '.swift'];
const TEXT_LIKE_MIMES = ['application/json', 'application/xml', 'application/javascript',
    'application/typescript', 'application/yaml', 'application/x-yaml',
    'application/sql', 'application/x-sh'];

const isTextLikeFile = (file) => {
    if (!file) return false;
    const mime = (file.type || '').toLowerCase();
    if (mime.startsWith('text/')) return true;
    if (TEXT_LIKE_MIMES.includes(mime)) return true;
    const name = (file.name || '').toLowerCase();
    return TEXT_LIKE_EXTS.some(ext => name.endsWith(ext));
};

// Per-file size cap when sending raw bytes (base64) to the refiner. Large files
// inflate the request and slow the round-trip; the extractor will truncate the
// extracted text anyway. Files over this go through as metadata only.
const REFINER_BINARY_CAP_BYTES = 8 * 1024 * 1024; // 8 MB

// Reads `file` as a base64 string (no data: prefix), suitable for the server's
// extract_context_files() pipeline.
const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            const idx = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1;
            resolve(idx >= 0 ? dataUrl.slice(idx + 1) : '');
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

// Build the attachment payload for the refiner from uploaded files. Each entry:
//   { name, type, size, content? (base64), preview? (text excerpt) }
// Choice between content / preview depends on what server-side extractor can do.
const buildRefinerAttachments = async (uploadedFiles) =>
    Promise.all(
        uploadedFiles.map(async (uf) => {
            const file = uf.data;
            const base = { name: uf.name, type: uf.type, size: file?.size };
            if (!file) return base;

            if (isExtractorFile(file) && file.size <= REFINER_BINARY_CAP_BYTES) {
                try {
                    const content = await readFileAsBase64(file);
                    return { ...base, content };
                } catch (e) {
                    console.warn(`Refiner: failed to read ${uf.name} as base64:`, e);
                    return base;
                }
            }

            if (isTextLikeFile(file)) {
                try {
                    const preview = await file.slice(0, 4000).text();
                    return { ...base, preview: preview || null };
                } catch (e) {
                    console.warn(`Refiner: failed to read ${uf.name} preview:`, e);
                    return base;
                }
            }

            return base; // unsupported binary (pptx, audio, video, oversized): metadata only
        })
    );

const ChatInputArea = ({
    inputMessage,
    isLoading,
    isRecording,
    uploadedFiles = [],
    reasoningMode = 'fast',
    defaultReasoningMode = null,
    chatSessionId,
    recentMessages = [],
    onInputChange,
    onSendMessage,
    onFileUpload,
    onStartRecording,
    onStopRecording,
    onReasoningModeChange,
    onPinReasoningMode,
    onOpenSavedPrompts
}) => {
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    // Drag and drop functionality
    const { isDragging, dragHandlers } = useDragAndDrop(onFileUpload);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage();
        }
    };

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        onFileUpload(e.target.files);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRecordingToggle = () => {
        if (isRecording) {
            onStopRecording();
        } else {
            onStartRecording();
        }
    };

    const removeFile = (index) => {
        // This would need to be implemented in the parent component
    };

    const adjustTextareaHeight = (textarea) => {
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 120);
        textarea.style.height = `${newHeight}px`;
    };

    // Handle paste events for images and files
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const files = [];

        // Iterate through clipboard items
        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Handle files (images, documents, etc.)
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }

        // If we found files, upload them
        if (files.length > 0) {
            e.preventDefault(); // Prevent default paste behavior
            onFileUpload(files);
        }
    };

    const canSend = inputMessage.trim() || uploadedFiles.length > 0;
    const hasContent = inputMessage.trim().length > 0;

    // JPRO Intelligence prompt refiner. Staged files are read on-demand (base64
    // for binary docs/images, short text excerpt for code/csv/etc.) so the
    // refiner can ground its rewrite in actual file content rather than just
    // names. Recent chat turns are forwarded the same way.
    const [isPreparingRefiner, setIsPreparingRefiner] = useState(false);
    const refiner = usePromptRefiner({
        draft: inputMessage,
        mode: 'chat',
        recentMessages,
        onAccept: (refined) => {
            onInputChange(refined);
            if (textareaRef.current) {
                textareaRef.current.focus();
                adjustTextareaHeight(textareaRef.current);
            }
        },
    });

    const handleRefineClick = async () => {
        if (!refiner.canOpen) return;
        setIsPreparingRefiner(true);
        try {
            const attachments = await buildRefinerAttachments(uploadedFiles);
            await refiner.open({ attachments });
        } finally {
            setIsPreparingRefiner(false);
        }
    };

    // Reset textarea height when input message changes (especially when cleared)
    useEffect(() => {
        if (textareaRef.current) {
            adjustTextareaHeight(textareaRef.current);
        }
    }, [inputMessage]);

    // Autofocus textarea on mount and when switching chat sessions
    useEffect(() => {
        if (textareaRef.current && !isRecording) {
            textareaRef.current.focus();
        }
    }, [chatSessionId]);

    return (
        <div className="relative border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900" {...dragHandlers}>

            {/* JPRO Intelligence refiner: questions modal renders fixed-positioned,
                preview card renders above the input container when active. */}
            {refiner.modal}

            {/* Main input area */}
            <div className="p-4">
                {refiner.preview && (
                    <div className="mb-3">{refiner.preview}</div>
                )}
                <div className={`relative bg-gray-50 dark:bg-gray-800 rounded-2xl border-2 transition-all duration-200 ${
                    isFocused || isRecording
                        ? 'border-blue-500 dark:border-blue-400 bg-white dark:bg-gray-750'
                        : isDragging
                            ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 border-dashed'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}>
                    {/* File upload UI disabled intentionally */}
                    
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                        multiple
                        accept="image/*,audio/*,application/pdf,.csv,.xls,.xlsx,.xlsm,.xlsb,.xltx,.xltm,.doc,.docx,.ppt,.pptx,.txt"
                    />
                    <div className="absolute left-3 bottom-3 flex items-center">
                        <button 
                            onClick={handleFileClick} 
                            title="Attach files" 
                            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 group"
                            disabled={isRecording}
                        >
                            <Paperclip size={18} className={`transition-colors duration-200 ${
                                isRecording 
                                    ? 'text-gray-300 dark:text-gray-600' 
                                    : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400'
                            }`} />
                        </button>
                    </div>
                   
                    
                    {/* Textarea */}
                    <textarea
                        ref={textareaRef}
                        value={inputMessage}
                        onChange={(e) => {
                            onInputChange(e.target.value);
                            adjustTextareaHeight(e.target);
                        }}
                        onKeyPress={handleKeyPress}
                        onPaste={handlePaste}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        placeholder={isRecording ? "🎤 Recording..." : isLoading ? "Add more info while Jpro is thinking..." : "Ask me anything..."}
                        rows="1"
                        disabled={isRecording}
                        className={`w-full pl-14 font-rasa pr-24 py-3 bg-transparent resize-both font-base leading-relaxed transition-all duration-200 ${
                            isRecording
                                ? 'text-red-600 dark:text-red-400 placeholder-red-400'
                                : 'text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400'
                        } focus:outline-none min-h-[50px] max-h-[200px] overflow-auto`}
                        style={{
                            minHeight: '50px',
                            resize: 'vertical'
                        }}
                        onInput={(e) => adjustTextareaHeight(e.target)}
                    />
                    
                    {/* Right side - Voice and Send buttons */}
                    <div className="absolute right-3 bottom-3 flex items-center gap-1">
                        {/* Saved prompts button */}
                        {onOpenSavedPrompts && !isRecording && (
                            <button
                                onClick={onOpenSavedPrompts}
                                title="Saved prompts"
                                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 group"
                            >
                                <BookmarkCheck size={18} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                            </button>
                        )}
                        {/* Voice recording button - better positioning */}
                        {!hasContent && (
                            <button 
                                onClick={handleRecordingToggle} 
                                title={isRecording ? "Stop recording" : "Start voice message"} 
                                className={`p-2 rounded-xl transition-all duration-200 ${
                                    isRecording 
                                        ? 'bg-red-500 hover:bg-red-600 text-white scale-110 animate-pulse' 
                                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 group'
                                }`}
                            >
                                {isRecording ? (
                                    <StopCircle size={18} />
                                ) : (
                                    <Mic size={18} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                                )}
                            </button>
                        )}
                        
                        {/* JPRO Intelligence refine: only when there's a draft to refine,
                            and only in the refiner's idle state (modal/preview replace it). */}
                        {hasContent && !isRecording && refiner.stage === 'idle' && (
                            <button
                                type="button"
                                onClick={handleRefineClick}
                                disabled={!refiner.canOpen || isPreparingRefiner}
                                title={isPreparingRefiner ? 'Reading attached files…' : 'Refine with JPRO Intelligence'}
                                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 group disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {(refiner.isPending || isPreparingRefiner) ? (
                                    <Loader2 size={18} className="text-blue-500 animate-spin" />
                                ) : (
                                    <Sparkles size={18} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-200" />
                                )}
                            </button>
                        )}

                        {/* Send button - improved design */}
                        {isLoading && !hasContent ? (
                            <div className="p-2 rounded-xl bg-gray-200 dark:bg-gray-700">
                                <div className="w-[18px] h-[18px] border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <button
                                onClick={onSendMessage}
                                disabled={!canSend}
                                title={isLoading ? "Add this info to current request" : "Send message"}
                                className={`p-2 rounded-xl transition-all duration-200 ${
                                    canSend
                                        ? isLoading
                                            ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95'
                                            : 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                {isLoading ? <Plus size={18} /> : <Send size={18} />}
                            </button>
                        )}
                    </div>
                </div>

                {/* Recording indicator - improved positioning */}
                {isRecording && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg py-2 px-4">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="font-medium">Recording in progress...</span>
                        <div className="flex gap-0.5">
                            <div className="w-1 h-3 bg-red-500 rounded-full animate-pulse" style={{animationDelay: '0ms'}}></div>
                            <div className="w-1 h-4 bg-red-500 rounded-full animate-pulse" style={{animationDelay: '150ms'}}></div>
                            <div className="w-1 h-3 bg-red-500 rounded-full animate-pulse" style={{animationDelay: '300ms'}}></div>
                        </div>
                    </div>
                )}

                {/* Helper text with character count and reasoning mode selector */}
                <div className="mt-2 flex items-center font-rasa justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{isLoading ? 'Jpro is thinking — type to add more context' : 'Press Enter to send, Shift+Enter for new line'}</span>
                    <div className="flex items-center gap-3">
                        {inputMessage.length > 0 && (
                            <span className={inputMessage.length > 15000 ? 'text-orange-500' : inputMessage.length > 18000 ? 'text-red-500' : ''}>
                                {inputMessage.length}/10000
                            </span>
                        )}
                        {/* Reasoning Mode Selector */}
                        <div className="flex items-center gap-2">
                            {reasoningMode === 'fast' ? (
                                <Zap size={12} className="text-orange-500" />
                            ) : (
                                <Brain size={12} className="text-blue-500" />
                            )}
                            <select
                                value={reasoningMode}
                                onChange={(e) => onReasoningModeChange?.(e.target.value)}
                                className="bg-transparent border-none cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none font-medium text-xs"
                                title={reasoningMode === 'fast' ? 'Fast Mode: Quick, factual responses (~5-8s)' : 'Deep Mode: Complex reasoning (~30-45s)'}
                            >
                                <option value="fast">Fast (Quick, Factual)</option>
                                <option value="deep">Deep (Complex Reasoning)</option>
                            </select>
                            {onPinReasoningMode && (
                                <button
                                    type="button"
                                    onClick={onPinReasoningMode}
                                    className={`p-0.5 rounded transition-colors ${
                                        defaultReasoningMode === reasoningMode
                                            ? 'text-blue-500 hover:text-blue-600'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                    }`}
                                    title={
                                        defaultReasoningMode === reasoningMode
                                            ? 'This is your default. Click to clear.'
                                            : `Set ${reasoningMode === 'fast' ? 'Fast' : 'Deep'} as your default reasoning mode`
                                    }
                                    aria-label="Pin reasoning mode as default"
                                >
                                    {defaultReasoningMode === reasoningMode ? (
                                        <Pin size={12} fill="currentColor" />
                                    ) : (
                                        <Pin size={12} />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Drag and drop overlay */}
            <DragDropOverlay isVisible={isDragging} size="default" />
        </div>
    );
};

export default ChatInputArea;